import { Component, inject, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FileSelectEvent, FileUploadModule } from "@openng/optimus-ui/fileupload";
import { DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { first } from "rxjs";
import { MessageService } from "@openng/optimus-ui/api";
import { HttpErrorResponse } from "@angular/common/http";
import { SelectChangeEvent, SelectModule } from "@openng/optimus-ui/select";
import { MessageModule } from "@openng/optimus-ui/message";
import { environment } from "../../../../../environments/environment";
import { GATE_CATALOG_ITEM_TYPES } from "../../../../enums/gateCatalogItemTypes.enum";
import { DropdownOption } from "../../../../models/ui/dropdownOption";
import { ImportService } from "../../../../services/api/import/import.service";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";

@Component({
  selector: "app-gate-catalog-import-dialog",
  standalone: true,
  imports: [
    FileUploadModule,
    ButtonComponent,
    SelectModule,
    TranslateModule,
    ReactiveFormsModule,
    TimeoutComponent,
    ProgressSpinnerModule,
    MessageModule,
  ],
  templateUrl: "./gate-catalog-import-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./gate-catalog-import-dialog.component.scss",
})
export class GateCatalogImportDialogComponent {
  private ref = inject(DynamicDialogRef);
  private importService = inject(ImportService);
  public translate = inject(TranslateService);
  private message = inject(MessageService);

  public form = new FormGroup({
    catalogItemType: new FormControl(GATE_CATALOG_ITEM_TYPES.MANUFACTURER, Validators.required),
  });

  public exampleFileDownloadLinks = {
    [GATE_CATALOG_ITEM_TYPES.MANUFACTURER]: `${environment.baseUrl}/import/example?exportExampleType=MANUFACTURER`,
    [GATE_CATALOG_ITEM_TYPES.MAKE]: `${environment.baseUrl}/import/example?exportExampleType=MODEL`,
    [GATE_CATALOG_ITEM_TYPES.ARTICLE]: `${environment.baseUrl}/import/example?exportExampleType=ARTICLE`,
  };

  private columnExplanations: Record<string, { header: string; explanation: string }[]> = {
    [GATE_CATALOG_ITEM_TYPES.MANUFACTURER]: [
      {
        header: "gate_components.import.explanations.MANUFACTURER.label.column_title",
        explanation: "gate_components.import.explanations.MANUFACTURER.label.explanation",
      },
      {
        header: "gate_components.import.explanations.MANUFACTURER.notes.column_title",
        explanation: "gate_components.import.explanations.MANUFACTURER.notes.explanation",
      },
      {
        header: "gate_components.import.explanations.MANUFACTURER.active.column_title",
        explanation: "gate_components.import.explanations.MANUFACTURER.active.explanation",
      },
    ],
    [GATE_CATALOG_ITEM_TYPES.MAKE]: [
      {
        header: "gate_components.import.explanations.MAKE.label.column_title",
        explanation: "gate_components.import.explanations.MAKE.label.explanation",
      },
      {
        header: "gate_components.import.explanations.MAKE.manufacturerName.column_title",
        explanation: "gate_components.import.explanations.MAKE.manufacturerName.explanation",
      },
      {
        header: "gate_components.import.explanations.MAKE.notes.column_title",
        explanation: "gate_components.import.explanations.MAKE.notes.explanation",
      },
      {
        header: "gate_components.import.explanations.MAKE.active.column_title",
        explanation: "gate_components.import.explanations.MAKE.active.explanation",
      },
    ],
    [GATE_CATALOG_ITEM_TYPES.ARTICLE]: [
      {
        header: "gate_components.import.explanations.ARTICLE.label.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.label.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.articleNumber.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.articleNumber.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.notes.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.notes.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.active.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.active.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.enableInspection.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.enableInspection.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.enableExchange.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.enableExchange.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.loadChanges.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.loadChanges.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.maxLoadChanges.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.maxLoadChanges.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.maxOperatingTime.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.maxOperatingTime.explanation",
      },
      {
        header: "gate_components.import.explanations.ARTICLE.makeName.column_title",
        explanation: "gate_components.import.explanations.ARTICLE.makeName.explanation",
      },
    ],
  };

  public loadingExampleFile = signal(false);
  public timeoutExampleFile = createTimeoutSignal(this.loadingExampleFile);

  public loadingImport = signal(false);
  public timeoutImport = createTimeoutSignal(this.loadingImport);
  public failedImport = signal(false);

  public selectedColumnExplanations = this.columnExplanations[GATE_CATALOG_ITEM_TYPES.MANUFACTURER];

  public catalogItemTypeOptions: DropdownOption[] = Object.values(GATE_CATALOG_ITEM_TYPES).map((catalogItemType) => ({
    label: `gate_components.import.catalog_type_options.${catalogItemType}`,
    value: catalogItemType,
  }));

  public failedCsvFilename: WritableSignal<string | null> = signal(null);
  public errorType: WritableSignal<"partial" | "error" | null> = signal(null);
  private selectedFile!: File;

  /**
   * Sets the selected file for the import to the file selected in the file upload.
   * @param event The file select event.
   */
  public onFileSelect(event: FileSelectEvent) {
    const file = event.files[0];
    this.selectedFile = file;
  }

  /**
   * Updates the selected column explanations based on the selected item type from the dropdown.
   * @param event The dropdown change event containing the new selected item type.
   */

  public changeType(event: SelectChangeEvent) {
    const newType = event.value;
    if (newType) {
      this.selectedColumnExplanations = this.columnExplanations[newType];
    }
  }

  /**
   * Saves the selected file and type as a new import.
   * Displays a message on success or error.
   * If the import is partial, displays a warning and sets errorType to "partial".
   * If the import fails, displays an error and sets errorType to "error".
   * If the import is successful, closes the dialog.
   */
  public save() {
    const importType = this.form?.get("catalogItemType")?.value;
    if (importType && this.selectedFile) {
      this.importService.createComponentsImport(this.selectedFile, importType).subscribe({
        next: (res) => {
          if (res.importFailedCsvFilename) {
            this.message.add({
              key: "global",
              severity: "warn",
              summary: this.translate.instant("gate_components.import.messages.partial"),
              life: 5000,
            });
            this.errorType.set("partial");
            this.failedCsvFilename.set(res.importFailedCsvFilename);
          } else {
            this.message.add({
              key: "global",
              severity: "success",
              summary: this.translate.instant("gate_components.import.messages.success"),
              life: 5000,
            });
            this.errorType.set(null);
            this.ref.close(importType);
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err.error?.importFailedCsvFilename) {
            this.errorType.set("error");
            this.failedCsvFilename.set(err.error.importFailedCsvFilename);
          }

          this.message.add({
            key: "global",
            severity: "error",
            summary: this.translate.instant("gate_components.import.messages.error"),
            life: 5000,
          });
        },
      });
    }
  }

  /**
   * Triggers the download of a CSV file that contains failed import data.
   * If a filename for the failed CSV is available, requests the file from the server,
   * creates a downloadable link, and programmatically clicks it to start the download.
   * @param event The click event to prevent default navigation behavior.
   */
  public downloadFailedFile(event: MouseEvent) {
    event.preventDefault();
    const filename = this.failedCsvFilename();
    if (filename) {
      this.importService
        .getComponentsFailedFile(filename)
        .pipe(first())
        .subscribe({
          next: (res) => {
            const blob = new Blob([res.body!], {
              type: "text/csv;charset=utf-8",
            });
            const link = document.createElement("a");
            link.download = filename;
            link.href = window.URL.createObjectURL(blob);
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
          },
        });
    }
  }

  /**
   * Closes the dialog and returns no value.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Downloads the template file based on the selected catalog item type.
   * Sets loading state during the download process.
   * If successful, triggers a download of the CSV file.
   */
  public downloadTemplate() {
    const importType = this.form?.get("catalogItemType")?.value;
    if (importType) {
      this.loadingExampleFile.set(true);
      this.importService
        .getComponentsExampleFile(importType)
        .pipe(first())
        .subscribe({
          next: (res) => {
            if (res) {
              const blob = new Blob([res.body!], {
                type: "text/csv;charset=utf-8",
              });
              const link = document.createElement("a");

              link.download = this.translate.instant(`gate_components.import.template_file_name.${importType}`);
              link.href = window.URL.createObjectURL(blob);
              link.style.display = "none";
              document.body.appendChild(link);

              link.click();
              this.loadingExampleFile.set(false);
              document.body.removeChild(link);
            }
          },
          error: () => {
            this.message.add({
              key: "global",
              severity: "error",
              summary: this.translate.instant("gate_components.import.download_template_failed"),
              life: 5000,
            });
            this.loadingExampleFile.set(false);
          },
        });
    }
  }
}
